import React, { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { useDragElement } from '../hooks/useDragElement';
import { zoomLevelStore } from '../store/zoomLevel';
import { MIN_SELECTION_SIZE, TIMELINE_COMPONENT_CLASS_IDENTIFIER } from '../constants';
import { getPlayerTimeFromSegmentTime, getSegmentAtCurrentFrame } from '../timeUtils';
import { ADD_MENU_POPOVER_TEST_ID } from '../TimelinePlayerActionBar/AddMenu/constants';
import CreateHookButton from './CreateHookButton';
import useCreateHookFromSelection from './useCreateHookFromSelection';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { defaultTimelineSelectionState, transcriptTimelineSelectionStore } from '@/stores/transcriptTimelineSelection';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import useThrottle from '@/hooks/useThrottle';

export default function TimelineSelect({
  compositeSegment,
  compositeSegmentRef,
  speakerTimelineContainerRef
}: {
  compositeSegment: CompositeSegment;
  compositeSegmentRef: React.RefObject<HTMLDivElement>;
  speakerTimelineContainerRef: React.RefObject<HTMLDivElement>;
}) {
  const { clipData, clipId, fps } = useClipsContext();

  const trimOffset = (compositeSegment?.currentBounds.fromFrame || 0) / fps;
  const [isCreatingSelection, setIsCreatingSelection] = useState(false);
  const selectionContainerRef = useRef<HTMLDivElement>(null);
  const [selectionContainerWidth, setSelectionContainerWidth] = useState(0);
  const initialSelectionStartRef = useRef<number | null>(null);
  const { seekTo } = useRemotionPlayerSeek();
  const { startTime, endTime, selectedDeleteIndex, startSegmentId, endSegmentId } = useSyncExternalStore(
    transcriptTimelineSelectionStore.subscribe,
    transcriptTimelineSelectionStore.getSnapshot
  );

  const selectionWidth = startTime !== null && endTime !== null ? Math.abs(startTime - endTime) : 0;
  const selectionLeft = startTime !== null && endTime !== null ? Math.min(startTime, endTime) : 0;

  useLayoutEffect(() => {
    setSelectionContainerWidth(selectionContainerRef.current?.offsetWidth ?? 0);
  }, [startTime, endTime, isCreatingSelection]);

  const { isAddHookVisible, handleCreateHookSegment } = useCreateHookFromSelection({
    clipData,
    clipId,
    startTime,
    endTime,
    startSegmentId,
    endSegmentId,
    isCreatingSelection,
    selectionContainerWidth
  });

  const throttledForceScrollEvent = useThrottle(
    (time: number, segmentId: string) =>
      EventBus.dispatch(CustomEvents.TimelineForcedScroll, {
        time,
        segmentId
      }),
    100
  );

  const { onMouseDown } = useDragElement({
    setDraggingElement: (dragId: string | null) => {
      setIsCreatingSelection(!!dragId);
    },
    setDraggingElementOffset: (offset: number) => {
      const startTime = initialSelectionStartRef.current;
      if (startTime === null) {
        return;
      }
      const offsetSize = Math.abs(offset) * fps * zoomLevelStore.getSnapshot().zoomLevel;
      if (offsetSize < MIN_SELECTION_SIZE) {
        transcriptTimelineSelectionStore.set(state => ({
          ...state,
          isSelecting: false
        }));
        return;
      }
      const time = startTime + offset;

      throttledForceScrollEvent(time, compositeSegment?.id);

      if (offset > 0) {
        transcriptTimelineSelectionStore.set(state => ({
          ...state,
          isSelecting: true,
          endTime: time,
          startTime: initialSelectionStartRef.current,
          startSegmentId: compositeSegment?.id,
          endSegmentId: compositeSegment?.id
        }));
      } else {
        transcriptTimelineSelectionStore.set(state => ({
          ...state,
          isSelecting: true,
          startTime: time,
          endTime: startTime,
          startSegmentId: compositeSegment?.id,
          endSegmentId: compositeSegment?.id
        }));
      }
    },
    onDragEnd: (offset: number) => {
      const latestStartTime = transcriptTimelineSelectionStore.getSnapshot().startTime;
      const latestEndTime = transcriptTimelineSelectionStore.getSnapshot().endTime;
      if (latestStartTime === null || latestEndTime === null) {
        transcriptTimelineSelectionStore.set(() => ({ ...defaultTimelineSelectionState }));
        return;
      }
      setIsCreatingSelection(false);
      const offsetSize = Math.abs(offset) * fps * zoomLevelStore.getSnapshot().zoomLevel;
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        isSelecting: false
      }));

      if (offsetSize < MIN_SELECTION_SIZE) {
        transcriptTimelineSelectionStore.set(() => ({
          ...defaultTimelineSelectionState
        }));
        return;
      }
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        startTime: Math.min(latestStartTime, latestEndTime),
        endTime: Math.max(latestStartTime, latestEndTime),
        endSegmentId: compositeSegment.id,
        startSegmentId: compositeSegment.id
      }));
      const actualStartTime = Math.min(latestStartTime, latestEndTime);
      const time = getPlayerTimeFromSegmentTime(actualStartTime, clipData, compositeSegment?.id);

      seekTo(time, false);
    },
    validDropZoneRef: compositeSegmentRef,
    droppableContainerRef: speakerTimelineContainerRef,
    preventResetOffsetOnDragEnd: true,
    fps
  });

  useEffect(() => {
    const compositeSegmentElement = compositeSegmentRef.current;
    if (compositeSegmentElement) {
      const handleMouseDown = (e: MouseEvent) => {
        if (
          !document
            .getElementsByClassName(TIMELINE_COMPONENT_CLASS_IDENTIFIER.FIXED_WIDTH_TIMELINE)[0]
            ?.contains(e.target as Node) ||
          (e.target as Element).closest(`[data-testid="${ADD_MENU_POPOVER_TEST_ID}"]`)
        ) {
          return;
        }

        if (!compositeSegmentElement.contains(e.target as Node)) {
          transcriptTimelineSelectionStore.set(() => ({
            ...defaultTimelineSelectionState
          }));
          return;
        }

        e.stopPropagation();
        e.preventDefault();

        const zoomLevel = zoomLevelStore.getSnapshot().zoomLevel;
        const left = compositeSegmentElement.getBoundingClientRect().left;

        const minOffset = (e.clientX - left) / zoomLevel / fps;

        if (!compositeSegment) {
          return;
        }

        const maxOffset = compositeSegmentElement.getBoundingClientRect().width / zoomLevel / fps - minOffset;
        transcriptTimelineSelectionStore.set(() => ({
          ...defaultTimelineSelectionState,
          startTime: minOffset + trimOffset,
          startSegmentId: compositeSegment.id,
          isSelecting: true
        }));
        initialSelectionStartRef.current = minOffset + trimOffset;

        onMouseDown(e, 'timeline-select', maxOffset, -minOffset);
      };

      document.addEventListener('mousedown', handleMouseDown);
      return () => {
        document.removeEventListener('mousedown', handleMouseDown);
      };
    }
  }, [clipData.asset_metadata.player_config, compositeSegment, compositeSegmentRef, onMouseDown, trimOffset, fps]);

  const initialDragPosition = useRef<number>(0);

  const { onMouseDown: onLeftDragStart } = useDragElement({
    setDraggingElement: () => {
      const startTime = transcriptTimelineSelectionStore.getSnapshot().startTime;
      if (startTime === null) {
        return;
      }
      initialDragPosition.current = startTime;
    },
    setDraggingElementOffset: (offset: number) => {
      const time = initialDragPosition.current + offset;
      const playerTime = getPlayerTimeFromSegmentTime(time, clipData, compositeSegment.id);
      const currentSegment = getSegmentAtCurrentFrame(playerTime, clipData);
      if (!currentSegment) {
        return;
      }
      throttledForceScrollEvent(time, currentSegment.id);
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        startTime: time,
        startSegmentId: currentSegment.id
      }));
    },
    onDragEnd: () => {
      const startTime = transcriptTimelineSelectionStore.getSnapshot().startTime;
      const startSegmentId = transcriptTimelineSelectionStore.getSnapshot().startSegmentId;
      if (startTime === null || startSegmentId === null) {
        return;
      }
      const time = getPlayerTimeFromSegmentTime(startTime, clipData, startSegmentId);
      seekTo(time);
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        isSelecting: false
      }));
    },
    validDropZoneRef: selectionContainerRef,
    droppableContainerRef: speakerTimelineContainerRef,
    preventResetOffsetOnDragEnd: false,
    fps
  });
  const { onMouseDown: onRightDragStart } = useDragElement({
    setDraggingElement: () => {
      const endTime = transcriptTimelineSelectionStore.getSnapshot().endTime;
      if (endTime === null) {
        return;
      }
      initialDragPosition.current = endTime;
    },
    setDraggingElementOffset: (offset: number) => {
      const time = initialDragPosition.current + offset;

      throttledForceScrollEvent(time, compositeSegment.id);
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        endTime: time,
        endSegmentId: compositeSegment.id
      }));
    },
    onDragEnd: () => {
      const endTime = transcriptTimelineSelectionStore.getSnapshot().endTime;
      if (endTime === null) {
        return;
      }
      const time = getPlayerTimeFromSegmentTime(endTime, clipData, compositeSegment?.id);

      seekTo(time);
      transcriptTimelineSelectionStore.set(state => ({
        ...state,
        isSelecting: false
      }));
    },
    validDropZoneRef: selectionContainerRef,
    droppableContainerRef: speakerTimelineContainerRef,
    preventResetOffsetOnDragEnd: false,
    fps
  });

  if (startTime === null || endTime === null || startTime === endTime || selectedDeleteIndex !== null) {
    return;
  }

  return (
    <>
      <CreateHookButton
        isVisible={isAddHookVisible}
        left={`calc(${(selectionLeft - trimOffset + selectionWidth / 2) * fps}px * var(--timeline-zoom) - 16px)`}
        top="40px"
        onCreate={handleCreateHookSegment}
      />
      <div
        className={`pointer-events-none absolute inset-x-0 h-[calc(100%-42px)] w-full`}
        style={{
          top: '38px',
          bottom: '12px'
        }}
      >
        <div
          ref={selectionContainerRef}
          className="group pointer-events-auto absolute z-50 size-full"
          style={{
            left: `calc(${(selectionLeft - trimOffset) * fps}px * var(--timeline-zoom))`,
            // 2px is added here to prevent dispatching a click event on elements behind the selection element.
            width: `calc(${selectionWidth * fps}px * var(--timeline-zoom) + 2px) `
          }}
        >
          <div className="timeline-selection absolute inset-0 mr-0.5 group-hover:outline group-hover:outline-offset-[-2px] group-hover:outline-red-500/50"></div>
          {!isCreatingSelection && (
            <>
              <div
                className="absolute inset-y-0 left-0 z-10 flex w-4 max-w-[50%] cursor-ew-resize items-center justify-center opacity-0 group-hover:opacity-100"
                onMouseDown={e => {
                  const minOffset = startTime - trimOffset;
                  const maxOffset = endTime - startTime;
                  transcriptTimelineSelectionStore.set(state => ({
                    ...state,
                    isSelecting: true
                  }));
                  onLeftDragStart(e, 'timeline-select', maxOffset, -minOffset);
                }}
                onClick={e => {
                  e.stopPropagation();
                }}
              >
                <div className="h-1/2 max-h-8 w-0.5 rounded bg-white"></div>
              </div>
              <div
                className="absolute inset-y-0 right-0 z-10 flex w-4 max-w-[50%] cursor-ew-resize items-center justify-center opacity-0 group-hover:opacity-100"
                onMouseDown={e => {
                  if (!compositeSegment) {
                    return;
                  }
                  transcriptTimelineSelectionStore.set(state => ({
                    ...state,
                    isSelecting: true
                  }));
                  const minOffset = endTime - startTime;
                  const maxOffset = (compositeSegment.currentBounds.toFrame || 0) / fps - endTime;

                  onRightDragStart(e, 'timeline-select', maxOffset, -minOffset);
                }}
                onClick={e => {
                  e.stopPropagation();
                }}
              >
                <div className="h-1/2 max-h-8 w-0.5 rounded bg-white"></div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
